import { useState } from 'react';
import api from '../api/axiosInstance';

/**
 * Custom hook for AI-powered product description generation
 * Uses Google Gemini 2.5 Flash to generate compelling product descriptions
 */
const useDescriptionGenerator = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  /**
   * Generate product description using AI
   * @param {Object} params - Generation parameters
   * @param {string} params.productName - Product name (required)
   * @param {string[]} params.categories - Category names
   * @param {Array} params.subcategories - Subcategory objects with subcategory property
   * @param {string} params.tone - Tone of description (professional, enthusiastic, minimal, luxury)
   * @returns {Promise<string|null>} Generated description or null on error
   */
  const generateDescription = async ({
    productName,
    categories = [],
    subcategories = [],
    tone = 'professional'
  }) => {
    setError('');
    setIsGenerating(true);

    try {
      // Validation
      if (!productName || !productName.trim()) {
        setError('Product name is required');
        setIsGenerating(false);
        return null;
      }

      if (categories.length === 0 && subcategories.length === 0) {
        setError('At least one category or subcategory is required');
        setIsGenerating(false);
        return null;
      }

      // Build payload (text-only, no images)
      const payload = {
        product_name: productName.trim(),
        categories: categories,
        subcategories: subcategories.map(s => s.subcategory || s),
        tone: tone
      };

      // Call API
      const response = await api.post('/ai/generate-description', payload);

      if (response.data.status === 'success') {
        setIsGenerating(false);
        return response.data.description;
      } else {
        throw new Error(response.data.message || 'Failed to generate description');
      }

    } catch (err) {
      console.error('Description generation error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to generate description';
      setError(errorMessage);
      setIsGenerating(false);
      return null;
    }
  };

  return {
    generateDescription,
    isGenerating,
    error,
    setError
  };
};

export default useDescriptionGenerator;
